import type { PublicDeliveryConfig } from './deliveryApi';

type CityRow = NonNullable<PublicDeliveryConfig['cities']>[number];

// Espaces insécables : coupé après le point, « Auxerre ·⏎Laborde » se lit comme deux villes.
const sectorLabel = (city: string, sector: string) => `${city}\u00A0·\u00A0${sector}`;

/**
 * SC-1168 — villes citées au client. Un secteur (hameau d'une commune,
 * `parent_city` renseigné) porte son propre minimum de commande : il se lit
 * juste après sa commune, sous le libellé du devis — « Auxerre · Laborde »
 * (SC-1205 ; il était tu tant que sa commune était listée). Si sa commune
 * n'est pas desservie, on l'écrit « Laborde (Auxerre) », sinon « Laborde »
 * seul n'orienterait personne.
 * Un ancien backend n'envoie pas `parent_city` : toutes les lignes sont des communes.
 */
export function servedCityNames(cities: CityRow[] | undefined): string[] {
  const rows = cities ?? [];
  const key = (name: string) => name.trim().toLowerCase();
  const sectorsOf = new Map<string, string[]>();
  for (const c of rows) {
    if (c.parent_city) sectorsOf.set(key(c.parent_city), [...(sectorsOf.get(key(c.parent_city)) ?? []), c.name]);
  }
  const communes = new Set(rows.filter((c) => !c.parent_city).map((c) => key(c.name)));
  const names: string[] = [];
  for (const c of rows) {
    if (c.parent_city) {
      if (!communes.has(key(c.parent_city))) names.push(`${c.name} (${c.parent_city})`);
      continue;
    }
    names.push(c.name);
    // Deux communes homonymes desservies : le secteur n'est cité qu'une fois.
    for (const sector of sectorsOf.get(key(c.name)) ?? []) names.push(sectorLabel(c.name, sector));
    sectorsOf.delete(key(c.name));
  }
  return names;
}

/** SC-1168 — « Auxerre · Laborde » quand le devis tombe dans un secteur, sinon la commune seule. */
export function quoteCityLabel(city: string | undefined, sector: string | undefined): string | undefined {
  return city && sector ? sectorLabel(city, sector) : city;
}
